// Visual page-builder helpers for the /admin/homepage editor.
// All builder config is stored under content.builder so the public Landing
// page and the admin editor render from the exact same data.

export const FONT_OPTIONS = [
  "Outfit", "Inter", "Manrope", "Poppins", "Montserrat", "Oswald",
  "Bebas Neue", "Anton", "Archivo", "Space Grotesk", "Playfair Display",
  "Sora", "Plus Jakarta Sans", "Rubik", "Teko", "League Spartan",
  "Syne", "Unbounded", "Figtree", "DM Sans",
];

export const WEIGHT_OPTIONS = [
  { label: "Thin", value: 200 },
  { label: "Light", value: 300 },
  { label: "Normal", value: 400 },
  { label: "Medium", value: 500 },
  { label: "Semibold", value: 600 },
  { label: "Bold", value: 700 },
  { label: "Extrabold", value: 800 },
  { label: "Black", value: 900 },
];

export const SECTION_LABELS = {
  nav: "Navbar", hero: "Hero", problem: "Before / After", features: "Features",
  try: "Live Demo", academy: "Academy", pricing: "Pricing",
  testimonials: "Testimonials", faq: "FAQ", finalCta: "Final CTA", footer: "Footer",
};

export const SECTION_DEFAULT_WIDTHS = {
  nav: 1280, hero: 1280, problem: 1280, features: 1280, try: 1024,
  academy: 1024, pricing: 1280, testimonials: 1280, faq: 768,
  finalCta: 1024, footer: 1280,
};

// Reorderable content sections (nav + footer stay fixed top/bottom).
export const ORDERED_SECTIONS = [
  "hero", "problem", "features", "try", "academy",
  "pricing", "testimonials", "faq", "finalCta",
];

export const rid = (p = "id") => p + "_" + Math.random().toString(36).slice(2, 9);

export function buildDefaultOrder(extraSections = []) {
  const o = ORDERED_SECTIONS.map((key) => ({ id: "ord_" + key, type: "curated", key }));
  extraSections.forEach((s) => o.push({ id: "ord_" + s.id, type: "custom", sid: s.id }));
  return o;
}

export function defaultBuilder() {
  return {
    canvasWidth: 1280,
    fontBody: "Outfit",
    sectionWidths: {},
    sectionStyles: {},
    hiddenSections: [],
    textStyles: {}, // { "hero.titleTop": { fontFamily, fontSize, fontWeight, italic, align, color, gradient } }
    extraSections: [],
    sectionOrder: buildDefaultOrder([]),
    heroVideo: { show: false, url: "" },
  };
}

export function mergeBuilder(content) {
  const d = defaultBuilder();
  const b = (content && content.builder) || {};
  const extraSections = b.extraSections || [];

  let order = Array.isArray(b.sectionOrder) ? b.sectionOrder.map((e) => ({ ...e })) : buildDefaultOrder(extraSections);
  // Reconcile order with custom sections.
  const sids = new Set(extraSections.map((s) => s.id));
  order = order.filter((e) => e.type !== "custom" || sids.has(e.sid));
  const have = new Set(order.filter((e) => e.type === "custom").map((e) => e.sid));
  extraSections.forEach((s) => { if (!have.has(s.id)) order.push({ id: "ord_" + s.id, type: "custom", sid: s.id }); });
  if (!order.some((e) => e.type === "curated")) {
    // Guard against an empty/corrupt order wiping the curated page.
    order = [...buildDefaultOrder([]), ...order.filter((e) => e.type === "custom")];
  }

  return {
    ...d,
    ...b,
    sectionWidths: { ...d.sectionWidths, ...(b.sectionWidths || {}) },
    sectionStyles: { ...d.sectionStyles, ...(b.sectionStyles || {}) },
    textStyles: { ...d.textStyles, ...(b.textStyles || {}) },
    hiddenSections: b.hiddenSections || [],
    extraSections,
    sectionOrder: order,
    heroVideo: { ...d.heroVideo, ...(b.heroVideo || {}) },
  };
}

export function newSection(type) {
  const id = rid("sec");
  if (type === "media") {
    return { id, type: "media", url: "", width: 1024, paddingY: 64, bg: "" };
  }
  return {
    id, type: "text", heading: "New section headline",
    body: "Double-click to edit this text. Use the sidebar to restyle, resize and duplicate this block.",
    width: 768, paddingY: 64, bg: "", align: "center",
  };
}

// Parse any media URL → a normalized embed descriptor.
export function parseMedia(url) {
  if (!url) return null;
  const u = String(url).trim();
  let m = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return { kind: "youtube", id: m[1], embed: `https://www.youtube.com/embed/${m[1]}` };
  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return { kind: "vimeo", id: m[1], embed: `https://player.vimeo.com/video/${m[1]}` };
  if (/(?:facebook\.com\/.+\/videos\/|facebook\.com\/watch\/?\?v=|fb\.watch\/)/.test(u)) {
    return { kind: "facebook", embed: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(u)}&show_text=false&autoplay=false` };
  }
  if (/\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(u)) return { kind: "video", src: u };
  if (/^https?:\/\//.test(u)) return { kind: "iframe", src: u };
  return null;
}

export function loadFont(family) {
  if (!family || family === "Outfit" || typeof document === "undefined") return;
  const id = "gf-" + family.replace(/\s/g, "");
  if (document.getElementById(id)) return;
  const l = document.createElement("link");
  l.id = id;
  l.rel = "stylesheet";
  l.href = `https://fonts.googleapis.com/css2?family=${family.replace(/\s/g, "+")}:ital,wght@0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,700&display=swap`;
  document.head.appendChild(l);
}
